import React, { useMemo, useState } from "react";
import { useAuthFetch } from "../lib/authFetch";
import { JsonTreeNode } from "./JsonTree";

// API Explorer (#80): exercise every agent-facing /query endpoint directly
// from the UI and inspect exactly what it returns. The catalog below mirrors
// indexing-worker/src/routes/query.ts — add an entry when adding a route.

const INDEXING_API = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";

interface ParamSpec {
  name: string;
  placeholder?: string;
  /** Shown under the input. */
  hint?: string;
}

interface EndpointSpec {
  id: string;
  group: string;
  title: string;
  method: "GET" | "POST" | "DELETE";
  /** Path with {param} placeholders. */
  path: string;
  description: string;
  pathParams?: ParamSpec[];
  queryParams?: ParamSpec[];
  /** Prefilled editable JSON body for POST endpoints. */
  bodyTemplate?: unknown;
}

const ENDPOINTS: EndpointSpec[] = [
  // ── Search ──────────────────────────────────────────────────────────────
  {
    id: "search",
    group: "Search",
    title: "Search chunks",
    method: "POST",
    path: "/query/search",
    description:
      "Full-text (+ optional vector) search over indexed chunks. groupByFile collapses hits per document; facetBy returns hit counts by facet; nodeIds restricts to specific sections.",
    bodyTemplate: {
      q: "search terms",
      perPage: 10,
      groupByFile: true,
      facetBy: ["categories"],
    },
  },
  {
    id: "search-probe",
    group: "Search",
    title: "Search probe",
    method: "POST",
    path: "/query/search/probe",
    description:
      "Scope a query without content: hits by type and distinct document count over the full match set, top documents by hit count, attribute distributions over any metadata key that occurs, and the published-date range. Same filters as search.",
    bodyTemplate: { q: "search terms", maxDocuments: 50 },
  },
  {
    id: "search-report",
    group: "Search",
    title: "Search report",
    method: "POST",
    path: "/query/search/report",
    description:
      "The agent reranking report: hits-by-type plus a per-document report joining document summary and node summaries to each hit. Same filters as search.",
    bodyTemplate: { q: "search terms", perPage: 20 },
  },
  {
    id: "chunks",
    group: "Search",
    title: "Get chunks",
    method: "POST",
    path: "/query/chunks",
    description:
      "Full stored text of up to 100 chunks by id (ids come from search hits, e.g. <fileId>-section-12). Invisible/unknown ids are reported in missing.",
    bodyTemplate: { ids: ["<fileId>-section-1"] },
  },
  {
    id: "similar",
    group: "Search",
    title: "Similar documents",
    method: "GET",
    path: "/query/similar/{fileId}",
    description: "Documents nearest to this one by document-level embedding.",
    pathParams: [{ name: "fileId" }],
    queryParams: [{ name: "perPage", placeholder: "10" }],
  },
  // ── Documents ───────────────────────────────────────────────────────────
  {
    id: "documents",
    group: "Documents",
    title: "List documents",
    method: "GET",
    path: "/query/documents",
    description:
      "Filterable document listing: type, metadata filters, publication-date range, coverage-period overlap.",
    queryParams: [
      { name: "corpusId", hint: "Narrow to one visible corpus" },
      { name: "documentType", placeholder: "Legal" },
      {
        name: "filters",
        placeholder: '[{"property":"author","comparator":"contains","value":"chase"}]',
        hint: "JSON array; comparators: equals | contains | matches",
      },
      { name: "publishedFrom", placeholder: "YYYY-MM-DD" },
      { name: "publishedTo", placeholder: "YYYY-MM-DD" },
      { name: "coverageFrom", placeholder: "YYYY-MM-DD" },
      { name: "coverageTo", placeholder: "YYYY-MM-DD" },
      { name: "page", placeholder: "1" },
      { name: "perPage", placeholder: "50" },
    ],
  },
  {
    id: "cards",
    group: "Documents",
    title: "Document cards",
    method: "POST",
    path: "/query/documents/cards",
    description:
      "Batch vetting digests for up to 50 fileIds: identity, dates, facets, AI summary blurb, page/section counts.",
    bodyTemplate: { fileIds: ["<fileId>"] },
  },
  {
    id: "outline",
    group: "Documents",
    title: "Document outline",
    method: "GET",
    path: "/query/outline/{fileId}",
    description: "Table of contents: root blurb plus one node per section with summary and page locator.",
    pathParams: [{ name: "fileId" }],
  },
  {
    id: "analysis",
    group: "Documents",
    title: "Document analysis",
    method: "GET",
    path: "/query/analysis/{fileId}",
    description: "The full AI analysis: summary, keywords, entities, dates, per-section summaries.",
    pathParams: [{ name: "fileId" }],
  },
  {
    id: "document",
    group: "Documents",
    title: "Document record",
    method: "GET",
    path: "/query/document/{fileId}",
    description: "The processed_documents row: filenames, S3 keys, status, metadata.",
    pathParams: [{ name: "fileId" }],
  },
  // ── Email links ─────────────────────────────────────────────────────────
  {
    id: "email-links",
    group: "Email links",
    title: "Email links (single)",
    method: "GET",
    path: "/query/documents/{fileId}/email-links",
    description:
      "Role (email / attachment / none), parent email for attachments, attachments for emails.",
    pathParams: [{ name: "fileId" }],
  },
  {
    id: "email-links-expand",
    group: "Email links",
    title: "Expand email links (batch)",
    method: "POST",
    path: "/query/documents/email-links/expand",
    description:
      "Deduplicated union of the input fileIds and all their email relatives, with `added` explaining each pull-in.",
    bodyTemplate: { fileIds: ["<fileId>"] },
  },
  // ── Tables ──────────────────────────────────────────────────────────────
  {
    id: "tables-search",
    group: "Tables",
    title: "Search table rows",
    method: "POST",
    path: "/query/tables/search",
    description:
      "Rows across all tables in up to 50 documents where any cell contains any term (case-insensitive substring). term may be a string or an array (OR).",
    bodyTemplate: { fileIds: ["<fileId>"], term: "uber", maxRows: 500 },
  },
  // ── Authors ─────────────────────────────────────────────────────────────
  {
    id: "authors-suggestions",
    group: "Authors",
    title: "Merge suggestions",
    method: "GET",
    path: "/query/authors/suggestions",
    description:
      "Merge candidates among observed authors: exact groups (Ltd = Limited — near-certain) and containment groups (need review). Nothing merges automatically.",
    queryParams: [{ name: "corpusId" }],
  },
  {
    id: "authors-merge",
    group: "Authors",
    title: "Merge authors",
    method: "POST",
    path: "/query/authors/merge",
    description:
      "Re-authors every document with a variant value to the canonical, and records variants as aliases — future documents snap deterministically. Optional hints feed the classifier prompt.",
    bodyTemplate: { corpusId: "<corpusId>", canonical: "CIA Insurance Services Limited", variants: ["CIA", "CIA Insurance", "CIA Insurance Services Ltd"], hints: [] },
  },
  {
    id: "authors-reassign",
    group: "Authors",
    title: "Reassign document author",
    method: "POST",
    path: "/query/authors/reassign",
    description: "Set one document's author; remember: true records the old value as an alias of the new (teach-back).",
    bodyTemplate: { fileId: "<fileId>", author: "Wards", remember: true },
  },
  {
    id: "authors-registry",
    group: "Authors",
    title: "Author registry",
    method: "GET",
    path: "/query/authors/registry",
    description: "The corpus's canonical authors with aliases and identification hints.",
    queryParams: [{ name: "corpusId" }],
  },
  // ── Datasets ────────────────────────────────────────────────────────────
  {
    id: "datasets-list",
    group: "Datasets",
    title: "List datasets",
    method: "GET",
    path: "/query/datasets",
    description: "Structured datasets in your writable corpora: purpose, column schema (source vs annotation), row counts.",
  },
  {
    id: "datasets-create",
    group: "Datasets",
    title: "Create dataset",
    method: "POST",
    path: "/query/datasets",
    description: "Create a corpus-owned dataset. Column class 'source' = extraction-owned; 'annotation' = user-owned.",
    bodyTemplate: {
      corpusId: "<corpusId>",
      name: "Transactions",
      purpose: "Every bank/card transaction across statements, one row per transaction.",
      columns: [
        { name: "date", type: "date", class: "source", required: true },
        { name: "description", type: "string", class: "source", required: true },
        { name: "amount", type: "money", class: "source", required: true },
        { name: "account_number", type: "string", class: "source" },
        { name: "category", type: "string", class: "annotation" },
      ],
    },
  },
  {
    id: "datasets-extract",
    group: "Datasets",
    title: "Extract from documents",
    method: "POST",
    path: "/query/datasets/{datasetId}/extract",
    description:
      "Deterministic row extraction from parsed tables: one LLM call per unseen table shape (mapping cached, editable), code applies it to every row. Re-runs upsert; annotations survive.",
    pathParams: [{ name: "datasetId" }],
    bodyTemplate: { documentType: "Account statement" },
  },
  {
    id: "datasets-rows",
    group: "Datasets",
    title: "Query rows",
    method: "POST",
    path: "/query/datasets/{datasetId}/rows",
    description: "Filter/sort/page rows; data and annotations merged; provenance per row. Array values are OR: contains any of the strings.",
    pathParams: [{ name: "datasetId" }],
    bodyTemplate: { filters: [{ column: "description", comparator: "contains", value: ["mortgage", "godiva", "bank of scotland"] }], perPage: 50 },
  },
  {
    id: "datasets-aggregate",
    group: "Datasets",
    title: "Aggregate",
    method: "POST",
    path: "/query/datasets/{datasetId}/aggregate",
    description: "groupBy + count and optional sum over a numeric column, with the same filters as rows.",
    pathParams: [{ name: "datasetId" }],
    bodyTemplate: { groupBy: "category", sumColumn: "amount" },
  },
  {
    id: "datasets-annotate",
    group: "Datasets",
    title: "Annotate rows",
    method: "POST",
    path: "/query/datasets/{datasetId}/annotate",
    description: "Set annotation values on rows selected by rowKeys or filters (bulk categorisation).",
    pathParams: [{ name: "datasetId" }],
    bodyTemplate: { filters: [{ column: "description", comparator: "contains", value: "uber" }], set: { category: "Travel" } },
  },
  {
    id: "datasets-columns",
    group: "Datasets",
    title: "Add annotation columns",
    method: "POST",
    path: "/query/datasets/{datasetId}/columns",
    description: "Dynamically add annotation-class columns (schema is data — no migration).",
    pathParams: [{ name: "datasetId" }],
    bodyTemplate: { columns: [{ name: "tax_returns", type: "string[]", description: "Return memberships, e.g. UK-2024/25" }] },
  },
  {
    id: "datasets-delete",
    group: "Datasets",
    title: "Delete dataset",
    method: "DELETE",
    path: "/query/datasets/{datasetId}",
    description:
      "Destroys the dataset, its rows, annotations, and cached mappings. Source documents untouched; rebuildable by re-extract, but annotations are gone for good.",
    pathParams: [{ name: "datasetId" }],
  },
  // ── Skills ──────────────────────────────────────────────────────────────
  {
    id: "skills-list",
    group: "Skills",
    title: "List skills",
    method: "GET",
    path: "/query/skills",
    description: "All extraction skills: status (active/draft), typed schemas, guidance, and linked corpus types.",
  },
  {
    id: "skills-induce",
    group: "Skills",
    title: "Induce skill draft",
    method: "POST",
    path: "/query/skills/induce",
    description:
      "Draft an extraction skill for a corpus type from its exemplar analyses (needs ≥3 summarised documents; write permission on the corpus). Drafts do nothing until approved.",
    bodyTemplate: { corpusId: "<corpusId>", typeName: "Invoice", sampleSize: 12 },
  },
  {
    id: "skills-approve",
    group: "Skills",
    title: "Approve skill",
    method: "POST",
    path: "/query/skills/{skillId}/approve",
    description:
      "Activate a draft and bind it to a corpus type. Version bump invalidates that type's cached analyses; re-extract via retrigger / corpus re-index.",
    pathParams: [{ name: "skillId", placeholder: "induced-<corpus>-<type>" }],
    bodyTemplate: { corpusId: "<corpusId>", typeName: "Invoice" },
  },
  {
    id: "skills-copy",
    group: "Skills",
    title: "Copy skill",
    method: "POST",
    path: "/query/skills/{skillId}/copy",
    description:
      "Copy a skill into another corpus as an independent skill (write permission on both sides). Optionally link the copy to a target type.",
    pathParams: [{ name: "skillId" }],
    bodyTemplate: { toCorpusId: "<corpusId>", typeName: "Invoice" },
  },
  {
    id: "corpus-types",
    group: "Skills",
    title: "Corpus types",
    method: "GET",
    path: "/query/corpus-types",
    description: "The document-type vocabulary of a corpus: definitions, usage counts, origin, linked skill.",
    queryParams: [{ name: "corpusId" }],
  },
  // ── Corpus metadata ─────────────────────────────────────────────────────
  {
    id: "document-properties",
    group: "Corpus metadata",
    title: "Document properties",
    method: "GET",
    path: "/query/document-properties",
    description: "Distinct metadata property keys with document counts — the legal filter vocabulary.",
  },
  {
    id: "category-values",
    group: "Corpus metadata",
    title: "Category values",
    method: "GET",
    path: "/query/category-values",
    description: "Known values per category type with document counts, scoped to visible corpora.",
    queryParams: [{ name: "category_type", placeholder: "economic_sector", hint: "Optional — omit for all types" }],
  },
  {
    id: "corpus-overview",
    group: "Corpus metadata",
    title: "Corpus overview",
    method: "GET",
    path: "/query/corpus-overview",
    description: "Per-corpus document counts, types, and date coverage.",
  },
  // ── Debug ───────────────────────────────────────────────────────────────
  {
    id: "typesense",
    group: "Debug",
    title: "Typesense records",
    method: "GET",
    path: "/query/typesense/{fileId}",
    description: "Raw Typesense entries (chunks + document entry) for a file.",
    pathParams: [{ name: "fileId" }],
  },
  {
    id: "costs",
    group: "Debug",
    title: "LLM costs",
    method: "GET",
    path: "/query/costs/{fileId}",
    description: "Recorded LLM/embedding costs for a file's processing runs.",
    pathParams: [{ name: "fileId" }],
  },
];

const METHOD_BADGE: Record<EndpointSpec["method"], string> = {
  GET: "bg-emerald-100 text-emerald-700",
  POST: "bg-amber-100 text-amber-700",
  DELETE: "bg-red-100 text-red-700",
};

interface ApiResponse {
  status: number;
  ok: boolean;
  ms: number;
  bytes: number;
  body: unknown;
}

const ApiExplorerPage: React.FC = () => {
  const authFetch = useAuthFetch();
  const [selectedId, setSelectedId] = useState<string>(ENDPOINTS[0].id);
  const endpoint = useMemo(() => ENDPOINTS.find((e) => e.id === selectedId)!, [selectedId]);

  const [pathValues, setPathValues] = useState<Record<string, string>>({});
  const [queryValues, setQueryValues] = useState<Record<string, string>>({});
  const [bodyText, setBodyText] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const selectEndpoint = (id: string) => {
    const ep = ENDPOINTS.find((e) => e.id === id)!;
    setSelectedId(id);
    setPathValues({});
    setQueryValues({});
    setBodyText(ep.bodyTemplate !== undefined ? JSON.stringify(ep.bodyTemplate, null, 2) : "");
    setError(null);
    setResponse(null);
  };

  const send = async () => {
    setError(null);
    setResponse(null);

    let path = endpoint.path;
    for (const p of endpoint.pathParams ?? []) {
      const v = (pathValues[p.name] ?? "").trim();
      if (!v) {
        setError(`Path parameter "${p.name}" is required`);
        return;
      }
      path = path.replace(`{${p.name}}`, encodeURIComponent(v));
    }

    const qs = new URLSearchParams();
    for (const p of endpoint.queryParams ?? []) {
      const v = (queryValues[p.name] ?? "").trim();
      if (v) qs.set(p.name, v);
    }
    const url = `${INDEXING_API}${path}${qs.toString() ? `?${qs}` : ""}`;

    let body: string | undefined;
    if (endpoint.method === "POST") {
      try {
        body = JSON.stringify(JSON.parse(bodyText));
      } catch (err) {
        setError(`Request body is not valid JSON: ${err instanceof Error ? err.message : err}`);
        return;
      }
    }

    setLoading(true);
    const started = performance.now();
    try {
      const res = await authFetch(url, {
        method: endpoint.method,
        ...(body !== undefined ? { headers: { "Content-Type": "application/json" }, body } : {}),
      });
      const text = await res.text();
      let parsed: unknown = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        // leave as raw text (e.g. proxy error pages)
      }
      setResponse({
        status: res.status,
        ok: res.ok,
        ms: Math.round(performance.now() - started),
        bytes: text.length,
        body: parsed,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const groups = useMemo(() => {
    const out: { group: string; endpoints: EndpointSpec[] }[] = [];
    for (const ep of ENDPOINTS) {
      const g = out.find((x) => x.group === ep.group);
      if (g) g.endpoints.push(ep);
      else out.push({ group: ep.group, endpoints: [ep] });
    }
    return out;
  }, []);

  const statusBadge = response
    ? response.ok
      ? "bg-emerald-100 text-emerald-700"
      : response.status >= 500
        ? "bg-red-100 text-red-700"
        : "bg-amber-100 text-amber-700"
    : "";

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Endpoint catalog */}
      <aside className="w-64 shrink-0 border-r border-krule bg-white overflow-y-auto">
        <div className="px-4 pt-5 pb-3">
          <h1 className="text-xl font-semibold text-ink">API Explorer</h1>
          <p className="text-xs text-kfaint font-mono mt-0.5">agent-facing /query endpoints</p>
        </div>
        {groups.map(({ group, endpoints }) => (
          <div key={group} className="pb-2">
            <div className="px-4 py-1 text-[11px] font-semibold uppercase tracking-wider text-kfaint">{group}</div>
            {endpoints.map((ep) => (
              <button
                key={ep.id}
                onClick={() => selectEndpoint(ep.id)}
                className={`w-full text-left px-4 py-1.5 flex items-center gap-2 text-sm transition-colors ${
                  ep.id === selectedId ? "bg-paper text-ink font-medium" : "text-kslate hover:bg-paper/60"
                }`}
              >
                <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${METHOD_BADGE[ep.method]}`}>
                  {ep.method}
                </span>
                <span className="truncate">{ep.title}</span>
              </button>
            ))}
          </div>
        ))}
      </aside>

      {/* Request + response */}
      <section className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${METHOD_BADGE[endpoint.method]}`}>
                {endpoint.method}
              </span>
              <code className="text-sm text-ink font-mono">{endpoint.path}</code>
            </div>
            <p className="text-sm text-kslate mt-1.5 leading-relaxed">{endpoint.description}</p>
          </div>

          {(endpoint.pathParams ?? []).length > 0 && (
            <div className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-kfaint">Path parameters</h2>
              {(endpoint.pathParams ?? []).map((p) => (
                <label key={p.name} className="block">
                  <span className="text-xs font-mono text-kslate">{p.name}</span>
                  <input
                    className="mt-1 w-full px-3 py-1.5 text-sm font-mono border border-krule rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-ksky"
                    value={pathValues[p.name] ?? ""}
                    placeholder={p.placeholder}
                    onChange={(e) => setPathValues((s) => ({ ...s, [p.name]: e.target.value }))}
                  />
                  {p.hint && <span className="text-[11px] text-kfaint">{p.hint}</span>}
                </label>
              ))}
            </div>
          )}

          {(endpoint.queryParams ?? []).length > 0 && (
            <div className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-kfaint">Query parameters (blank = omitted)</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {(endpoint.queryParams ?? []).map((p) => (
                  <label key={p.name} className="block">
                    <span className="text-xs font-mono text-kslate">{p.name}</span>
                    <input
                      className="mt-1 w-full px-3 py-1.5 text-sm font-mono border border-krule rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-ksky"
                      value={queryValues[p.name] ?? ""}
                      placeholder={p.placeholder}
                      onChange={(e) => setQueryValues((s) => ({ ...s, [p.name]: e.target.value }))}
                    />
                    {p.hint && <span className="text-[11px] text-kfaint">{p.hint}</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {endpoint.method === "POST" && (
            <div className="space-y-1.5">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-kfaint">Request body (JSON)</h2>
              <textarea
                className="w-full h-44 px-3 py-2 text-sm font-mono border border-krule rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-ksky resize-y"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                spellCheck={false}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={send}
              disabled={loading}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-ink text-white hover:bg-ink/85 disabled:opacity-50 transition-colors"
            >
              {loading ? "Sending…" : "Send request"}
            </button>
            {error && <span className="text-sm text-red-600">{error}</span>}
          </div>

          {response && (
            <div className="rounded-lg border border-krule overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-paper/50 border-b border-krule">
                <div className="flex items-center gap-3 text-xs">
                  <span className={`font-bold px-2 py-0.5 rounded ${statusBadge}`}>{response.status}</span>
                  <span className="text-kfaint font-mono">{response.ms} ms · {(response.bytes / 1024).toFixed(1)} KB</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRaw((v) => !v)}
                    className="text-[11px] font-mono px-2 py-0.5 rounded border border-krule text-kslate hover:bg-paper transition-colors"
                  >
                    {showRaw ? "tree" : "raw"}
                  </button>
                  <button
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(response.body, null, 2))}
                    className="text-[11px] font-mono px-2 py-0.5 rounded border border-krule text-kslate hover:bg-paper transition-colors"
                  >
                    copy
                  </button>
                </div>
              </div>
              <div className="px-3 py-2.5 font-mono text-xs overflow-x-auto max-h-[32rem] overflow-y-auto">
                {showRaw ? (
                  <pre className="whitespace-pre-wrap">{JSON.stringify(response.body, null, 2)}</pre>
                ) : (
                  <JsonTreeNode name={null} value={response.body} depth={0} openDepth={2} />
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ApiExplorerPage;
